import { inject, Injectable } from '@angular/core';
import { EvidenceDocumentService } from 'lemunzo-api';
import { map, Observable } from 'rxjs';
import { MediaViewerContentType } from '../enums/media-viewer-content-types';
import { base64ToBlob } from '../utils/base64-to-blob';
import { mimeToMediaViewerContentType } from '../utils/mime-to-media-viewer-content-type';

export interface EvidenceDocumentPreview {
    contentType: MediaViewerContentType;
    /** Object URL for the fetched file — caller owns it and must revoke it when done. */
    url: string;
}

/**
 * Evidence has no preview URL endpoint: the file is fetched as base64 and exposed
 * through a temporary object URL for the media viewer. Shared by every consumer
 * that previews an evidence document (currently the shared evidence table's own dialog
 * preview, and the claim-detail evidence panel's inline preview) so there's one
 * implementation of the fetch → content-type-check → blob → object-URL sequence.
 */
@Injectable({ providedIn: 'root' })
export class EvidenceDocumentPreviewService {
    private readonly evidenceDocumentService = inject(EvidenceDocumentService);

    /** Emits `null` when the file's content type isn't previewable. */
    load(
        evidenceDocumentId: number
    ): Observable<EvidenceDocumentPreview | null> {
        return this.evidenceDocumentService
            .getEvidenceDocument(evidenceDocumentId)
            .pipe(
                map((file) => {
                    const contentType = mimeToMediaViewerContentType(
                        file.contentType
                    );

                    if (contentType == null) {
                        return null;
                    }

                    const blob = base64ToBlob(file.data, file.contentType);

                    return { contentType, url: URL.createObjectURL(blob) };
                })
            );
    }
}
